import { ref, computed, onMounted, type Ref } from "vue";

import { useAppConfig } from "@core/composables/data/useAppConfig";
import { profileService } from "@core/services/profileService";
import type { UserProfile } from "@core/types/models";

interface ProfileState {
  backendProfile: Ref<UserProfile | null>;
  loading: Ref<boolean>;
  error: Ref<string | null>;
  loadPromise: Promise<void> | null;
}

const createProfileState = (): ProfileState => ({
  backendProfile: ref<UserProfile | null>(null),
  loading: ref(false),
  error: ref<string | null>(null),
  loadPromise: null
});

let sharedProfileState: ProfileState | null = null;

const getProfileState = () => {
  sharedProfileState ??= createProfileState();
  return sharedProfileState;
};

export function useProfile(options?: { refresh?: boolean }) {
  const state = getProfileState();
  const { config } = useAppConfig();
  const isAuthenticated = config.isAuthenticated;

  const profile = computed<UserProfile>(() => state.backendProfile.value ?? config.userProfile);

  const setBackendProfile = (nextProfile: UserProfile | null) => {
    state.backendProfile.value = nextProfile;
  };

  async function loadProfile() {
    if (!isAuthenticated) {
      return;
    }

    if (state.loadPromise) {
      return state.loadPromise;
    }

    state.loadPromise = (async () => {
      try {
        state.loading.value = true;
        state.error.value = null;
        const freshProfile = await profileService.fetchProfile();
        setBackendProfile(freshProfile);
      } catch (err) {
        state.error.value = "profile.loadErrorMessage";
        console.error("Error loading profile:", err);
      } finally {
        state.loading.value = false;
        state.loadPromise = null;
      }
    })();

    return state.loadPromise;
  }

  if (options?.refresh) {
    onMounted(() => {
      if (isAuthenticated) {
        void loadProfile();
      }
    });
  }

  return {
    profile,
    loading: state.loading,
    error: state.error,
    refresh: loadProfile,
    setProfile: setBackendProfile
  };
}
